// /festive-alerts — Festival Alerts Dashboard (PAGES.md §3.88)
//
// Smart Festival Alert Engine dashboard. Proactive alerts based on calendar
// festivals, local events, order trends, past sales data. Alert categories:
// preparation (30/15/7 days), inventory (shortage predictions), pre-order
// activation, revenue opportunity with historical comparison.
// RESTAURANT vertical only — MANAGER+ (level 30). Feature flag: ENABLE_FESTIVE_MODULE.
// Traceability: FR-FEST-007, FR-FEST-008, FR-FEST-009

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Tabs,
  Toggle,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { festiveService } from '@/services/festive.service';
import type { FestiveAlert } from '@/services/festive.service';
import { GLASS, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { ApiError } from '@/lib/api-client';
import { URGENCY_STYLE, formatDate } from './constants';
import type { BadgeVariant } from './constants';
import {
  BellIcon, BellEmptyIcon, CalendarIcon, AlertTriangleIcon,
  ShoppingBagIcon, TrendingUpIcon, ClockIcon,
} from './icons';

const CATEGORY_ICON: Record<string, typeof BellIcon> = {
  PREPARATION: ClockIcon,
  STOCK_CHECK: AlertTriangleIcon,
  CAPACITY_WARNING: AlertTriangleIcon,
  BOOKING_DEADLINE: ShoppingBagIcon,
  REMINDER: TrendingUpIcon,
};

const ALERT_TABS = [
  { id: 'all', label: 'All' },
  { id: 'PREPARATION', label: 'Preparation' },
  { id: 'STOCK_CHECK', label: 'Stock' },
  { id: 'BOOKING_DEADLINE', label: 'Booking' },
  { id: 'CAPACITY_WARNING', label: 'Capacity' },
  { id: 'REMINDER', label: 'Reminders' },
];

const PRIORITY_VARIANT: Record<string, BadgeVariant> = {
  CRITICAL: 'danger',
  HIGH: 'danger',
  MEDIUM: 'warning',
  LOW: 'info',
};

function priorityToUrgencyStyle(priority: string | undefined) {
  if (priority === 'CRITICAL' || priority === 'HIGH') return URGENCY_STYLE.URGENT;
  if (priority === 'MEDIUM') return URGENCY_STYLE.UPCOMING;
  return URGENCY_STYLE.INFORMATIONAL;
}

function formatLabel(value: string | undefined | null): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ');
}

// ─── Alert Card ─────────────────────────────────────────────────────────────

function AlertCard({
  alert,
  onDismiss,
}: {
  alert: FestiveAlert;
  onDismiss: () => void;
}) {
  const urgency = priorityToUrgencyStyle(alert.priority);
  const CategoryIcon = CATEGORY_ICON[alert.alert_type] ?? BellIcon;
  const priorityVariant = PRIORITY_VARIANT[alert.priority] ?? 'info';
  const daysUntil = Number(alert.days_until);

  return (
    <div className={cn(
      'rounded-2xl p-5 transition-all duration-200',
      urgency.bg,
      'backdrop-blur-sm shadow-[0_8px_24px_-14px_rgba(15,23,42,0.15)] dark:shadow-[0_8px_24px_-14px_rgba(0,0,0,0.35)]',
      alert.acknowledged ? 'opacity-50' : '',
      'animate-slide-up',
    )}>
      <div className="flex items-start gap-4">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0', urgency.icon, urgency.bg)}>
          <CategoryIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-bold text-neutral-900 dark:text-neutral-100">{alert.festival_name}</p>
              <p className="text-sm text-neutral-700 dark:text-neutral-300 mt-0.5">{alert.message}</p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <Badge variant={priorityVariant} size="sm">{alert.priority ?? 'INFO'}</Badge>
              {Number.isFinite(daysUntil) && daysUntil >= 0 && (
                <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                  {daysUntil}d
                </span>
              )}
            </div>
          </div>

          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2 italic">
            {formatLabel(alert.alert_type)}
          </p>

          <div className="flex items-center gap-2 mt-3">
            {!alert.acknowledged && (
              <Button variant="secondary" size="sm" onClick={onDismiss}>Dismiss</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function FestiveAlertsContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') ?? 'all';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(tab === 'all' ? {} : { tab }, { replace: true });
  }, [setSearchParams]);

  const [alerts, setAlerts] = useState<FestiveAlert[]>([]);
  const [alertHistory, setAlertHistory] = useState<FestiveAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ── Alert preferences (spec: Toggle per category) ──────────────────────
  const [prefPreparation, setPrefPreparation] = useState(true);
  const [prefInventory, setPrefInventory] = useState(true);
  const [prefPreOrder, setPrefPreOrder] = useState(true);
  const [prefRevenue, setPrefRevenue] = useState(true);

  useEffect(() => { document.title = 'Festival Alerts — Orio360 Admin'; }, []);

  const fetchAlerts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await festiveService.getAlerts({
        tab: activeTab === 'all' ? undefined : activeTab,
      });
      setAlerts(data);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load alerts.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, toast]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // ── Fetch alert history (spec: GET /api/v1/festivals/alerts/history) ──
  const fetchHistory = useCallback(async () => {
    try {
      const data = await festiveService.getAlertHistory();
      setAlertHistory(data);
    } catch {
      // Non-critical — history table is supplementary
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // ── Alert history columns (spec: DataTable alert history) ──────────────
  const historyColumns: ColumnDef<FestiveAlert>[] = useMemo(() => [
    {
      key: 'created_at', header: 'Date',
      render: (_v: unknown, row: FestiveAlert) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {formatDate(row.created_at)}
        </span>
      ),
    },
    {
      key: 'festival_name', header: 'Festival',
      render: (_v: unknown, row: FestiveAlert) => (
        <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{row.festival_name}</span>
      ),
    },
    {
      key: 'alert_type', header: 'Type',
      render: (_v: unknown, row: FestiveAlert) => (
        <Badge variant="draft" size="sm">{formatLabel(row.alert_type)}</Badge>
      ),
    },
    {
      key: 'message', header: 'Message',
      render: (_v: unknown, row: FestiveAlert) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300 truncate max-w-xs block">{row.message}</span>
      ),
    },
    {
      key: 'acknowledged', header: 'Action',
      render: (_v: unknown, row: FestiveAlert) => (
        <Badge variant={row.acknowledged ? 'draft' : 'warning'} size="sm">
          {row.acknowledged ? 'Dismissed' : 'Pending'}
        </Badge>
      ),
    },
  ], []);

  const handleDismiss = useCallback(async (alertId: string) => {
    try {
      await festiveService.dismissAlert(alertId);
      setAlerts((prev) => prev.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a)));
      toast({ title: 'Alert dismissed', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to dismiss.', variant: 'error' });
    }
  }, [toast]);

  // ── Derived stats ─────────────────────────────────────────────────────
  const activeAlerts = alerts.filter((a) => !a.acknowledged);
  const urgentCount = activeAlerts.filter((a) => a.priority === 'CRITICAL' || a.priority === 'HIGH').length;
  const upcomingFestivals = new Set(alerts.map((a) => a.festival_id).filter(Boolean)).size;

  return (
    <PageContainer
      title="Festival Alerts"
      subtitle="Proactive alerts for upcoming festivals and operational readiness"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Festival Alerts' },
      ]}
    >
      <div className="animate-fade-in">
        {/* ═══ KPI Cards ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="animate-slide-up">
            <StatCard title="Upcoming Festivals" value={String(upcomingFestivals)} icon={<CalendarIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
            <StatCard title="Active Alerts" value={String(activeAlerts.length)} icon={<BellIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <StatCard
              title="Urgent"
              value={String(urgentCount)}
              icon={<AlertTriangleIcon className="h-4 w-4" />}
              variant={urgentCount > 0 ? 'trend' : 'default'}
              trend={urgentCount > 0 ? { direction: 'up', value: `${urgentCount}`, positive: false } : undefined}
            />
          </div>
        </div>

        {/* ═══ Tabs ═══ */}
        <Tabs items={ALERT_TABS} activeId={activeTab} onChange={setActiveTab} />

        <div className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={cn(GLASS_INTERACTIVE, 'p-5')}>
                  <div className="flex gap-4">
                    <Skeleton variant="rect" width={40} height={40} />
                    <div className="flex-1 space-y-2">
                      <Skeleton variant="text" width="30%" height={18} />
                      <Skeleton variant="text" width="80%" height={14} />
                      <Skeleton variant="text" width="50%" height={12} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : alerts.length === 0 ? (
            <EmptyState
              variant="page"
              icon={<BellEmptyIcon />}
              title="No festival alerts"
              description="The system will alert you when festivals or events are approaching"
            />
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onDismiss={() => handleDismiss(alert.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ═══ Alert Preferences (spec: Toggle per category) ═══ */}
        <div className={cn(GLASS, 'p-5 mt-6')}>
          <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-4">
            Alert Preferences
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-700 dark:text-neutral-300">Preparation</span>
              <Toggle checked={prefPreparation} onChange={() => setPrefPreparation((p) => !p)} aria-label="Toggle preparation alerts" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-700 dark:text-neutral-300">Inventory</span>
              <Toggle checked={prefInventory} onChange={() => setPrefInventory((p) => !p)} aria-label="Toggle inventory alerts" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-700 dark:text-neutral-300">Pre-Order</span>
              <Toggle checked={prefPreOrder} onChange={() => setPrefPreOrder((p) => !p)} aria-label="Toggle pre-order alerts" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-700 dark:text-neutral-300">Revenue</span>
              <Toggle checked={prefRevenue} onChange={() => setPrefRevenue((p) => !p)} aria-label="Toggle revenue alerts" />
            </div>
          </div>
        </div>

        {/* ═══ Alert History (spec: DataTable alert history) ═══ */}
        {alertHistory.length > 0 && (
          <div className={cn(GLASS, 'overflow-hidden mt-6 hidden sm:block')}>
            <div className="px-5 py-4">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                Alert History ({alertHistory.length})
              </h3>
            </div>
            <DataTable<FestiveAlert> columns={historyColumns} data={alertHistory} />
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export function FestiveAlertsPage() {
  return (
    <ErrorBoundary>
      <FestiveAlertsContent />
    </ErrorBoundary>
  );
}
